/**
 * `/alerts` — WP-L5, Task 53. `web/shapes/alerts.md` is the agreed shape.
 *
 * The queue an incident handler works from. Its order answers *"what has fired that
 * nobody has put their name against, and which of those is worst"* rather than "what
 * happened", which is what a log viewer answers.
 *
 * ── Nothing is requested before an access reason exists ─────────────────────
 *
 * `GET /alerts` is classified sensitive access: one successful call writes one
 * `wp_data_access_logs` row naming this operator, the employees on the page and the
 * reason, and `X-Access-Reason` is **mandatory in v1** — no policy table can turn it
 * off. So `path` is `null` until the gate is satisfied, which is `useApiQuery`'s own
 * "there is nothing to ask yet" signal, and the screen fires no request that it knows
 * would be refused. See `web/src/access/reason.ts` for why that is a design decision
 * and not a convenience.
 *
 * ── Every narrowing is a server parameter ───────────────────────────────────
 *
 * `severity`, `status` and `trigger` go in the query string. The endpoint is
 * **cursor**-paginated and publishes no total, so a client-side filter would be
 * answering "…among these fifty" with nothing to notice the error against. What the
 * screen prints is what it read, labelled as such, with `has_more` rendered as
 * `PartialData`.
 *
 * ── One list, one refusal surface ───────────────────────────────────────────
 *
 * Both the read and the acknowledgement render `ApiErrorNote`, which branches on
 * `code` and prints the server's own sentence wherever API_CONTRACT §5.1 marks it
 * safe. Task 45's `12bb350` fixed twenty-seven places where a refusal never reached
 * the screen; nothing here is allowed to become the twenty-eighth.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { formatInstantWithAge } from '../i18n/datetime';
import { useSession } from '../auth/SessionProvider';
import { alertAcknowledgePath, alertsPath } from '../api/paths';
import type {
  AlertAcknowledgeRequest,
  AlertListResponse,
  AlertSeverity,
  AlertStatus,
  AlertSummary,
} from '../api/types';
import { useApiMutation } from '../api/useApiMutation';
import { useApiQuery } from '../api/useApiQuery';
import { accessHeaders } from '../access/reason';
import { AccessReasonGate } from '../access/AccessReasonGate';
import { bySeverityThenNewest } from '../alerts/inbox';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { EmptyState } from '../states/EmptyState';
import { PartialData } from '../states/PartialData';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { ScreenHeading } from '../shell/ScreenHeading';
import { AcknowledgePanel } from './alerts/AcknowledgePanel';
import { AlertRow } from './alerts/AlertRow';
import { SeverityFilter } from './alerts/SeverityFilter';

const PAGE_LIMIT = 50;

export function AlertsScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const [reason, setReason] = useState('');
  const [severity, setSeverity] = useState<AlertSeverity | null>(null);
  const [status, setStatus] = useState<AlertStatus | null>(null);
  const [selected, setSelected] = useState<AlertSummary | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';
  const mayResolve = (user?.permissions ?? []).includes('workforce.resolve_incidents');

  const headers = accessHeaders(reason);
  const path =
    headers === undefined
      ? null
      : alertsPath({
          limit: PAGE_LIMIT,
          severity: severity ?? undefined,
          status: status ?? undefined,
        });

  const inbox = useApiQuery<AlertListResponse>(
    path,
    reloadToken,
    headers === undefined ? {} : { headers },
  );

  /**
   * The acknowledgement carries the same reason.
   *
   * `POST /alerts/{id}/acknowledge` is sensitive access too — the `200` body is a
   * whole `Alert` — and the gate follows the response content, not the HTTP verb.
   * Otherwise the write path would be a way to read a protected record and leave no
   * access-log trace, which is what SEC-64 exists to prevent.
   */
  const acknowledge = useApiMutation<AlertAcknowledgeRequest, AlertSummary>(
    // A placeholder path: the target is chosen per press, so every `submit` passes an
    // override. This constant is never the URL of a real request.
    alertAcknowledgePath('unset'),
    'POST',
    headers ?? {},
  );

  const rows: readonly AlertSummary[] = useMemo(
    () => [...(inbox.data?.data ?? [])].sort(bySeverityThenNewest),
    [inbox.data],
  );
  const newest = rows.reduce<string | null>(
    (worst, row) => (worst === null || row.triggered_at > worst ? row.triggered_at : worst),
    null,
  );

  const submitAck = (note: string): void => {
    if (selected === null) return;
    acknowledge
      .submit(note.trim() === '' ? {} : { note: note.trim() }, alertAcknowledgePath(selected.id))
      .then(() => {
        // Re-read **unconditionally after a settled submit** would re-read on a
        // refusal too, which is the bug `policies.test.tsx` caught by counting reads.
        // `acknowledge.data` is set only by a `200`, so this branch cannot be reached
        // by a refusal rather than being guarded against one.
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; present so a change there cannot
           become an unhandled rejection. */
      });
  };

  // Only a success re-reads the queue, and only once per success.
  const acknowledged = acknowledge.data;
  const [lastAcked, setLastAcked] = useState<string | null>(null);
  if (acknowledged !== null && acknowledged.id !== lastAcked) {
    setLastAcked(acknowledged.id);
    setReloadToken((value) => value + 1);
  }

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.alerts')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('alerts.intro')}</p>

      <AccessReasonGate
        value={reason}
        subject={t('alerts.subjectWord')}
        testId="l5-alerts-access"
        onOpen={(next) => {
          setReason(next);
          setSelected(null);
          acknowledge.reset();
        }}
      />

      {reason === '' ? (
        <StatusNote severity="info" title={t('access.closed.title')} testId="l5-alerts-closed">
          <p>{t('access.closed.body', { subject: t('alerts.subjectWord') })}</p>
        </StatusNote>
      ) : (
        <>
          {/* The scope sentence. `resolve_incidents` admits without widening: the
              field of view still comes from the §7.1 view ladder, so a department
              manager sees their tree and — fail-closed — no alert with no
              `employee_id` at all, which is every device- and fleet-shaped one. A
              short queue must not be read as a quiet week.

              It sits **above** the filters rather than under them: it is a statement
              about what this operator can ever see, not about what they have narrowed
              to, and a reader who meets it after the controls reads it as a caption
              on the filter they just set. */}
          <p className="wp-small wp-muted" data-testid="l5-alerts-scope">
            {t('alerts.scopeNote')}
          </p>

          <SeverityFilter
            severity={severity}
            status={status}
            onSeverity={(value) => {
              setSeverity(value);
              setSelected(null);
            }}
            onStatus={(value) => {
              setStatus(value);
              setSelected(null);
            }}
          />

          {inbox.status === 'loading' ? (
            <Skeleton
              rows={4}
              variant="block"
              label={t('alerts.subjectWord')}
              testId="l5-alerts-skeleton"
            />
          ) : null}

          {inbox.status === 'error' && inbox.error !== null ? (
            <ApiErrorNote
              error={inbox.error}
              onRetry={inbox.reload}
              subject={t('alerts.subjectWord')}
              testId="l5-alerts-error"
            />
          ) : null}

          {inbox.data === null ? null : (
            <>
              {/* Not `LastSync`. That component states two specific provenances —
                  the newest **agent heartbeat** and achau's last successful sync —
                  and neither is what this line reports. What a queue's reader needs
                  is when the newest thing on it fired, and the sentence says so;
                  borrowing a component whose label names a different source would be
                  a screen rendering a value whose provenance it has misstated. There
                  is no endpoint that publishes the evaluation engine's last tick, so
                  the screen does not claim one. */}
              <p className="wp-small wp-muted" data-testid="l5-alerts-read">
                {newest === null
                  ? t('alerts.read.none')
                  : t('alerts.read.newest', {
                      when: formatInstantWithAge(newest, locale, timezone, t('common.never')),
                    })}
              </p>
              {inbox.data.page.has_more ? (
                <PartialData
                  excludes={t('alerts.partial.excludes', { count: rows.length })}
                  testId="l5-alerts-partial"
                />
              ) : null}

              {rows.length === 0 ? (
                <EmptyState
                  title={t('alerts.empty.title')}
                  description={t('alerts.empty.body', {
                    severity: severity ?? t('filters.all'),
                    status: status ?? t('filters.all'),
                  })}
                  testId="l5-alerts-empty"
                />
              ) : (
                <ul className="wp-c-ruled" data-testid="l5-alert-list">
                  {rows.map((alert) => (
                    <Fragment key={alert.id}>
                      <AlertRow
                        alert={alert}
                        timezone={timezone}
                        mayResolve={mayResolve}
                        onAcknowledge={(next) => {
                          acknowledge.reset();
                          setSelected(next);
                        }}
                      />
                      {/* The acknowledgement opens **under the row it is about**,
                          inside the same list, rather than as a panel at the foot of
                          a fifty-row queue or as a modal. Nothing here needs
                          protected focus — the five facts are on screen and the
                          operator is meant to be able to look back up at the row. */}
                      {selected?.id === alert.id ? (
                        <li className="wp-c-ruled__drawer">
                          <AcknowledgePanel
                            alert={selected}
                            pending={acknowledge.status === 'pending'}
                            error={acknowledge.error}
                            result={acknowledge.data}
                            onSubmit={submitAck}
                            onCancel={() => {
                              setSelected(null);
                              acknowledge.reset();
                            }}
                          />
                        </li>
                      ) : null}
                    </Fragment>
                  ))}
                </ul>
              )}

              {/* Same guard as /incidents: if the acknowledged row has left the page
                  the filter returns, its panel — and the confirmation on it — stays
                  below the queue rather than disappearing with the row. */}
              {selected !== null && !rows.some((one) => one.id === selected.id) ? (
                <AcknowledgePanel
                  alert={selected}
                  pending={acknowledge.status === 'pending'}
                  error={acknowledge.error}
                  result={acknowledge.data}
                  onSubmit={submitAck}
                  onCancel={() => {
                    setSelected(null);
                    acknowledge.reset();
                  }}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
